import type { ActivityType } from '@tryhackme/interfaces';
import { Header, Modal, useViewport } from '@tryhackme/thm-ui-components';
import { differenceInDays, parseISO } from 'date-fns';
import DOMPurify from 'dompurify';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'src/app/hooks';
import { LanguageToggle } from 'src/common/components/language-toggle';
import { SimpleSnow } from 'src/common/components/simple-snow';
import { StreakFreezeModalContent } from 'src/common/components/streak-freeze/streak-freeze';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { UserOccupation } from 'src/common/constants/user';
import { ThemeColorMode } from 'src/common/enums';
import { CertificationType } from 'src/common/enums/certification';
import { useFeatureIsOn } from 'src/common/growthbook';
import { B2BUserHelper } from 'src/common/helpers';
import { useCurrentRoute } from 'src/common/hooks';
import { useRepairStreak } from 'src/common/hooks/use-repair-streak';
import { useGetUserQuery, useTrackEventMutation } from 'src/common/slices';
import { CertificationAppBanner } from 'src/features/certifications/components/certification-app-banner/certification-app-banner';
import { UseIsNavItemActive } from 'src/features/header/hooks/use-is-nav-item-active';
import { AccessNetworks } from 'src/features/room/components/access-networks';
import bannerBackground from 'src/images/svgs/certifications/certificate-banner-bg.svg';
import pt1BannerBackground from 'src/images/svgs/certifications/pt1-banner-bg.svg';
import { BookNowModal } from '../business/landing-page/components';
import { BigStreakShareModal, shouldShowBigStreakShareModal } from '../room/components/share-big-streak-modal';
import { StreakShareModal } from '../room/components/share-streak-modal';
import { SignupModal } from '../signup/components/signup-modal';
import { AppBanner, SearchResultsPopUp, SelfLikeEasterEgg, useAppBannerPermittedRoute, useShowAppBanner } from './components';
import { avatarDropdownItems, getAdminLinks, IS_BIG_STREAK_MILESTONE_DISMISSED } from './constants';
import { StyledHeader } from './header.styles';
import type { HeaderWrapperProps } from './header.types';
import { useGetUserNotificationsQuery, useLikeNotificationMutation, useMarkNotificationAsReadMutation, useUnlikeNotificationMutation, useUpdateReadNotificationsMutation } from './notifications/notifications.slice';
import { addTrackingToNotifications, categorizeNotifications } from './notifications/notifications.utils';
import { useMainNavigationLinks } from './use-main-navigation-links';
export const HeaderWrapper: React.FC<HeaderWrapperProps> = ({
  isHeaderTransparent = false,
  isHeaderSticky = true,
  headerDisplayNone = false,
  stickToTop = true,
  isFullWidth = false
}) => {
  const route = useCurrentRoute();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    width
  } = useViewport();
  const isMobile = width < BREAKPOINTS.MOBILE;
  const isHomepageRoute = route.pathname === '/';
  const source = searchParams.get('source');
  const [trackEvent] = useTrackEventMutation();
  const [hasStreakFreezeWonNotification, setHasStreakFreezeWonNotification] = useState(false);
  const [hasBigStreakMilestoneNotification, setHasBigStreakMilestoneNotification] = useState(false);
  const [shownOnce, setShownOnce] = useState(false);
  const [streakShareModal, setStreakShareModal] = useState({
    streak: 0,
    open: false
  });
  const [isSlidingPaneOpen, setIsSlidingPaneOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState<boolean>(false);
  const [isSearchResultsPopUpOpen, setIsSearchResultsPopUpOpen] = useState(false);
  const isSearchResultsPopUpOpenRef = useRef(isSearchResultsPopUpOpen);
  const [isBookNowOpen, setIsBookNowOpen] = useState(false);
  const [isLogoHovering, setIsLogoHovering] = useState(false);
  const isAocThemeEnabled = useFeatureIsOn('aoc-2025');
  useEffect(() => {
    isSearchResultsPopUpOpenRef.current = isSearchResultsPopUpOpen;
  }, [isSearchResultsPopUpOpen]);
  const {
    tutorPanelOpen,
    vmPanelOpen
  } = useAppSelector(state => state.splitScreen);
  const splitScreenOpen = tutorPanelOpen || vmPanelOpen;
  const {
    data,
    isSuccess: isUserLoggedIn
  } = useGetUserQuery();
  const {
    data: notifications
  } = useGetUserNotificationsQuery(undefined, {
    skip: !data
  });
  const [updateReadNotifications] = useUpdateReadNotificationsMutation();
  const [likeNotification] = useLikeNotificationMutation();
  const [unlikeNotification] = useUnlikeNotificationMutation();
  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const userData = data?.data?.user;
  const expiringCardData = userData?.cardDetails;
  const cardExpiryDate = expiringCardData?.cardExpiryDate && parseISO(expiringCardData.cardExpiryDate);
  const daysUntilExpiry = cardExpiryDate && differenceInDays(cardExpiryDate, new Date());
  const isCardExpiringWithin180Days = daysUntilExpiry && daysUntilExpiry > 0 && daysUntilExpiry <= 180;
  const isCardExpired = daysUntilExpiry && daysUntilExpiry <= 0;
  const avatar = userData?.avatar ?? '';
  const streak = userData?.streak;
  const repairStreakHandler = useRepairStreak();
  const isPremium = userData?.isPremium;
  const hasCompanyId = userData?.subscriptions?.some(sub => sub?.companyId != null);
  const isPaused = userData?.isPaused && !hasCompanyId;
  const {
    isAdminAndB2BUser,
    isEduUser
  } = B2BUserHelper(userData);
  const isShowBookDemoCta = !(isAdminAndB2BUser || hasCompanyId || isEduUser);
  const isCyberPractitionerOrManager = [UserOccupation.CYBER_PRACTITIONER, UserOccupation.MANAGER].includes(userData?.demographics?.occupation as UserOccupation);
  const isDisplayGoBusinessCta = useFeatureIsOn('display-go-business-cta');
  const isShowGoBusinessCta = route.pathname !== '/business' && isPremium && isCyberPractitionerOrManager && isDisplayGoBusinessCta;
  const allUserNotifications = (notifications?.data?.notifications ?? []).filter(Boolean).map(result => {
    const sanitizedDescription = DOMPurify.sanitize(result.description);
    const notification = {
      ...result,
      description: sanitizedDescription
    };
    return notification;
  });

  // FF: extend-social-activities (frontend gate)
  const isExtendedSocialActivitiesOn = useFeatureIsOn('extend-social-activities');
  const visibleUserNotifications = useMemo(() => {
    if (isExtendedSocialActivitiesOn) return allUserNotifications;
    return allUserNotifications.filter(n => {
      if (!n) return false;
      const isActivity = n.groupId === 'user-activity';
      const t = n.activityData?.activityType as ActivityType | undefined;
      const isExtendedType = t === 'badge_earned' || t === 'league_won';
      return !(isActivity && isExtendedType);
    });
  }, [allUserNotifications, isExtendedSocialActivitiesOn]);
  const {
    specialUserNotifications,
    userNotificationsWithTracking
  } = useMemo(() => {
    const {
      specialUserNotifications,
      userNotifications
    } = categorizeNotifications(visibleUserNotifications);
    const userNotificationsWithTracking = addTrackingToNotifications(userNotifications, trackEvent);
    return {
      specialUserNotifications,
      userNotificationsWithTracking
    };
  }, [visibleUserNotifications, trackEvent]);
  useEffect(() => {
    if (specialUserNotifications.length > 0 && shownOnce === false) {
      const hasWonStreakFreeze = specialUserNotifications.some(notification => notification.groupId === 'streakFreezeWon');
      if (hasWonStreakFreeze) {
        setShownOnce(true);
        setHasStreakFreezeWonNotification(hasWonStreakFreeze);
        // This localStorage is legacy support, remove when room page is moved to react
        // This won't work in localhost because its not same port number
        window.localStorage.removeItem('isAwardedStreakFreeze');
      }
    }
  }, [specialUserNotifications, shownOnce]);
  const unreadNotificationsCount = visibleUserNotifications?.filter(notification => notification && !notification.read).length;
  const handleNotificationsOpen = () => {
    trackEvent({
      event: 'open-notifications'
    });
  };
  const forcedThemeForNav = isMobile ? ThemeColorMode.DARK : route?.handle?.forcedTheme;
  const navItems = useMainNavigationLinks(forcedThemeForNav);
  const isDashboardRoute = route.pathname.startsWith('/dashboard');
  // Reset snow when leaving dashboard or when FF is off
  useEffect(() => {
    if (!(isAocThemeEnabled && isDashboardRoute)) {
      setIsLogoHovering(false);
    }
  }, [isAocThemeEnabled, isDashboardRoute]);
  const handlerAccessMachine = () => {
    setIsSlidingPaneOpen(true);
  };
  const onClickSignup = () => {
    // Safely get the current pathname. Falls back to '/' if the window is undefined
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    localStorage.setItem('signupReferrer', pathname);
    if (isHomepageRoute) {
      setIsSignupModalOpen(true);
      return;
    }
    const newParams = new URLSearchParams(searchParams);
    if (isMobile) newParams.set('mobile', 'true');
    navigate({
      pathname: '/signup',
      search: newParams ? `?${newParams.toString()}` : ''
    });
  };
  const handleGoBusiness = () => {
    trackEvent({
      event: 'click-go-business'
    });
    navigate({
      pathname: '/business'
    });
  };
  const remAttackBoxBtn = useAppSelector(state => state.room.remAttackBoxBtn);
  const hasUserJoinedRoom = useAppSelector(state => state.room.hasUserJoinedRoom);
  const attackBoxPrivateIP = useAppSelector(state => state.splitScreen.attackBoxPrivateIP);
  const showAccessMachinesButton = isUserLoggedIn && hasUserJoinedRoom && !remAttackBoxBtn && !splitScreenOpen;
  const urgencyIndicator = isCardExpiringWithin180Days || isCardExpired || false;
  const [isShowAppBanner, hideAppBanner] = useShowAppBanner();
  const isBannerPermittedRoute = useAppBannerPermittedRoute(route);
  const showAppBanner = isShowAppBanner && isBannerPermittedRoute;
  const isPT1ExamEnabled = useFeatureIsOn('pt1-exam');
  const isFrenchLanguageEnabled = useFeatureIsOn('is-french-language');
  const isStreakShareEnabled = useFeatureIsOn('streak-share');
  const isManagementDashboardV3Enabled = useFeatureIsOn('mgt-dashboard-v3');
  useEffect(() => {
    const handleGlobalSearchHotkey = (event: KeyboardEvent) => {
      const isSearchHotkey = (event.metaKey || event.ctrlKey) && event.key === 'k';
      if (!isSearchHotkey) return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const tagName = target.tagName;
      const isEditable = target.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
      if (isEditable && !isSearchResultsPopUpOpenRef.current) return;
      event.preventDefault();
      setIsSearchResultsPopUpOpen(prev => {
        if (!prev && splitScreenOpen) return prev;
        return !prev;
      });
    };
    document.addEventListener('keydown', handleGlobalSearchHotkey);
    return () => {
      document.removeEventListener('keydown', handleGlobalSearchHotkey);
    };
  }, [splitScreenOpen]);

  // Show language toggle only on specific paths
  const allowedLanguageTogglePaths = ['/', '/login', '/signup', '/login/sso'];
  const normalizedPathname = route.pathname.replace(/\/+$/, '') || '/';
  const shouldShowLanguageToggle = isFrenchLanguageEnabled && allowedLanguageTogglePaths.includes(normalizedPathname) && navigator.language?.toLowerCase()?.includes('fr');
  const streakLength = streak?.streak ?? 0;
  const hasBigStreak = shouldShowBigStreakShareModal(streakLength);
  if (hasBigStreak && !hasBigStreakMilestoneNotification) {
    setHasBigStreakMilestoneNotification(true);
  }
  const handleLike = async (notificationId: string, activityType: ActivityType) => {
    const result = await likeNotification({
      notificationId
    }).unwrap();
    trackEvent({
      event: 'like-notification',
      properties: {
        activityType
      }
    }).catch(() => {});
    return result.data;
  };
  const isUserReactionsEnabled = useFeatureIsOn('user-reactions');
  const handleReactionHover = (_notificationId: string, activityType: ActivityType, hoverDurationMs: number) => {
    if (!isUserReactionsEnabled) return;
    trackEvent({
      event: 'reaction-hover-over-100ms',
      properties: {
        notification_type: activityType,
        hover_duration_ms: hoverDurationMs,
        user_id: userData?._id
      }
    }).catch(() => {});
  };
  const handleUnlike = async (notificationId: string, activityType: ActivityType) => {
    const result = await unlikeNotification({
      notificationId
    }).unwrap();
    trackEvent({
      event: 'unlike-notification',
      properties: {
        activityType
      }
    }).catch(() => {});
    return result.data;
  };
  const handleHover = (groupId: string, activityType?: ActivityType) => {
    trackEvent({
      event: 'hover-notification',
      properties: {
        activityType,
        groupId
      }
    }).catch(() => {});
  };
  const handleBookDemo = () => {
    trackEvent({
      event: 'click-book-demo',
      properties: {
        source: 'header'
      }
    });
    setIsBookNowOpen(true);
  };
  const forceFullWidth = route.pathname.includes('client') && isManagementDashboardV3Enabled;
  useEffect(() => {
    const currentPathname = route.pathname;
    if (currentPathname && isSearchResultsPopUpOpenRef.current) {
      setIsSearchResultsPopUpOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.pathname]);
  return <StyledHeader $isTransparent={isHeaderTransparent} $showBanner={showAppBanner} $headerDisplayNone={headerDisplayNone} $stickToTop={stickToTop} data-sentry-element="StyledHeader" data-sentry-component="HeaderWrapper" data-sentry-source-file="header.tsx">
      {showAppBanner && isPT1ExamEnabled && <AppBanner dismiss={hideAppBanner} isUserLoggedIn={isUserLoggedIn} backgroundImage={isPT1ExamEnabled ? pt1BannerBackground : bannerBackground}>
          <CertificationAppBanner type={CertificationType.PT1} />
        </AppBanner>}
      <Header isSearchResultsPopUpOpen={isSearchResultsPopUpOpen} setIsSearchResultsPopUpOpen={setIsSearchResultsPopUpOpen} avatar={avatar} avatarDropDownMenuItems={avatarDropdownItems(userData?.username, urgencyIndicator)} isAuth={!!data} isChristmas={false} streak={streak} onStreakRepair={repairStreakHandler} onStreakShare={streak => setStreakShareModal({
      streak,
      open: true
    })} navItems={navItems} isPremium={isPremium} isPaused={isPaused} clearNotifications={() => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      updateReadNotifications();
    }} accessMachineButton={{
      isVisible: showAccessMachinesButton,
      value: attackBoxPrivateIP
    }} notifications={userNotificationsWithTracking} unreadNotificationsCount={unreadNotificationsCount} onNotificationsOpen={handleNotificationsOpen} adminLinks={getAdminLinks({
      roles: userData?.roles,
      accountType: userData?.accountType
    })} handlerAccessMachine={handlerAccessMachine} splitScreenMode={splitScreenOpen} isNavItemActive={UseIsNavItemActive} onClickSignup={onClickSignup} isExtendedSearchEnabled={!splitScreenOpen} searchResults={<SearchResultsPopUp setIsSearchResultsPopUpOpen={setIsSearchResultsPopUpOpen} />} searchLink="/hacktivities/search" isTransparent={isHeaderTransparent} isSticky={isHeaderSticky} aria-label="search for hacktivities" source={source} languageSelector={shouldShowLanguageToggle ? <LanguageToggle /> : null} isFullWidth={isFullWidth || forceFullWidth} isStreakShareEnabled={isStreakShareEnabled} requestingUser={userData?.username ?? ''} onLike={handleLike} onUnlike={handleUnlike} onHover={handleHover} enableReactionsTooltip={!isMobile && isUserReactionsEnabled} onReactionHover={handleReactionHover} renderSelfLikeEasterEgg={onComplete => <SelfLikeEasterEgg onComplete={onComplete} />} isShowBookDemoCta={isShowBookDemoCta} handleBookDemo={handleBookDemo} handleGoBusiness={isShowGoBusinessCta ? handleGoBusiness : undefined} onLogoHoverChange={hovering => {
      const eligible = isAocThemeEnabled && isDashboardRoute;
      setIsLogoHovering(eligible && hovering);
    }} data-sentry-element="Header" data-sentry-source-file="header.tsx" />

      <Modal preventOnOpenAutoFocus onOpenChange={open => setHasStreakFreezeWonNotification(open)} defaultOpen={false} open={hasStreakFreezeWonNotification && !hasBigStreakMilestoneNotification} data-sentry-element="Modal" data-sentry-source-file="header.tsx">
        <StreakFreezeModalContent data-sentry-element="StreakFreezeModalContent" data-sentry-source-file="header.tsx" />
      </Modal>

      <BigStreakShareModal username={userData?.username} streakLength={streakLength} isShareModalOpen={hasBigStreakMilestoneNotification} handleCloseButtonAction={() => {
      setHasBigStreakMilestoneNotification(false);
      localStorage.setItem(`${IS_BIG_STREAK_MILESTONE_DISMISSED}_${streakLength}`, 'true');
    }} data-sentry-element="BigStreakShareModal" data-sentry-source-file="header.tsx" />

      {isStreakShareEnabled && <StreakShareModal username={userData?.username} streakLength={streakShareModal.streak} isShareModalOpen={streakShareModal.open} handleCloseButtonAction={() => setStreakShareModal(prevState => ({
      ...prevState,
      open: false
    }))} />}

      <AccessNetworks isSlidingPaneOpen={isSlidingPaneOpen} setIsSlidingPaneOpen={setIsSlidingPaneOpen} machineIP={attackBoxPrivateIP} data-sentry-element="AccessNetworks" data-sentry-source-file="header.tsx" />
      <SignupModal open={isSignupModalOpen} onOpenChange={setIsSignupModalOpen} data-sentry-element="SignupModal" data-sentry-source-file="header.tsx" />
      <BookNowModal forceDarkMode open={isBookNowOpen} onOpenChange={setIsBookNowOpen} title="Book a demo" description="Enter your details to book a demo. We'll be in touch to schedule a time." data-sentry-element="BookNowModal" data-sentry-source-file="header.tsx" />

      {isAocThemeEnabled && isDashboardRoute && <div style={{
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1000
    }}>
          <SimpleSnow spawnFromTop isActive={isLogoHovering} fadeOutDurationMs={300} useCssFade cssFadeDurationMs={300} speedMultiplier={2.8} flakeCount={15} radiusPx={2.4} blurPx={0} opacity={0.8} coastDurationMs={120} />
          <SimpleSnow spawnFromTop isActive={isLogoHovering} fadeOutDurationMs={300} useCssFade cssFadeDurationMs={300} speedMultiplier={1.8} flakeCount={20} radiusPx={1.2} blurPx={0} opacity={0.99} coastDurationMs={120} />
        </div>}
    </StyledHeader>;
};